import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './StepLogin.module.scss';
import setupStyles from '../../../pages/BusinessSetup/BusinessSetup.module.scss';
import { getApiErrorMessage } from '../../../helpers/apiErrors';
import { useAuthClient } from '../../../helpers/clientInfo';
import { useForgotPasswordUsernameAndPasswordMutation } from '../../../api/apiSlice';
import { trackAnalyticsEvent } from '../../../helpers/analytics';
import { AccountType, AccountTypes, AgencyTermsAcceptance, RegistrationCompliancePayload } from '../../../models';

type AuthMode = 'register' | 'login';
type AuthModeScope = 'both' | 'registerOnly' | 'loginOnly';
type StepLoginLocationState = {
  authMode?: AuthMode;
};
type StepLoginProps = {
  className?: string;
  stepLabel?: string;
  title?: string;
  description?: string;
  inlineNote?: string;
  authPanelEyebrow?: string;
  authPanelTitle?: string;
  authPanelClassName?: string;
  authPanelVariant?: 'card' | 'plain';
  showAuthPanelHeader?: boolean;
  registerBackAction?: {
    label?: string;
    onClick: () => void;
  };
  modeScope?: AuthModeScope;
  frameVariant?: 'default' | 'bare';
  showIntro?: boolean;
  googlePlacement?: 'beforeForm' | 'afterForm';
  accountType?: AccountType;
};

const PASSWORD_REQUIREMENTS_MESSAGE =
  'Password must be at least 8 characters and include at least one lowercase letter, one uppercase letter, and one number.';

const createEmptyCredentials = () => ({ email: '', password: '', confirmPassword: '' });
const createEmptyAgencyCompliance = () => ({
  companyName: '',
  acceptedTerms: false,
  acceptedBusinessOnlyTerms: false,
});

const getDefaultMode = (modeScope: AuthModeScope): AuthMode =>
  modeScope === 'loginOnly' ? 'login' : 'register';

const isModeAllowed = (modeScope: AuthModeScope, mode: AuthMode): boolean => {
  if (modeScope === 'both') {
    return true;
  }

  return modeScope === 'loginOnly' ? mode === 'login' : mode === 'register';
};

const meetsPasswordRequirements = (password: string) => {
  if (!password) {
    return false;
  }

  const hasMinLength = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);

  return hasMinLength && hasLowercase && hasUppercase && hasNumber;
};

const getErrorStatusCode = (error: any): number | undefined => {
  const status =
    error?.response?.status ??
    error?.status ??
    error?.originalStatus ??
    error?.data?.status ??
    error?.data?.statusCode;

  return typeof status === 'number' ? status : undefined;
};

const isUnauthorizedError = (error: any) => {
  if (getErrorStatusCode(error) === 401) {
    return true;
  }

  const message = typeof error?.message === 'string' ? error.message : '';
  const rawError = typeof error?.error === 'string' ? error.error : '';

  return /status code 401\b/i.test(message) || /\b401\b/.test(rawError);
};

const isNotFoundError = (error: any) => getErrorStatusCode(error) === 404;

const isConflictError = (error: any) => getErrorStatusCode(error) === 409;

const getAuthSource = (pathname: string, modeScope: AuthModeScope) => {
  if (pathname === '/quick-setup') return 'quick_setup';
  if (pathname === '/sign-in') return 'sign_in_page';
  if (pathname === '/get-started') return 'business_setup';

  return modeScope === 'registerOnly'
    ? 'registration_only'
    : modeScope === 'loginOnly'
      ? 'login_only'
      : 'auth_step';
};

const StepLogin: React.FC<StepLoginProps> = ({
  className,
  stepLabel = 'Step 1',
  title = 'Create your account',
  description = 'Sign in if you already have a workspace, or create a new account to start setup.',
  inlineNote = 'New users continue setup here. Existing users go straight to the portal.',
  authPanelEyebrow,
  authPanelTitle,
  authPanelClassName: authPanelExtraClassName,
  authPanelVariant = 'card',
  showAuthPanelHeader = true,
  registerBackAction,
  modeScope = 'both',
  frameVariant = 'default',
  showIntro = true,
  googlePlacement = 'afterForm',
  accountType,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedAuthMode = (location.state as StepLoginLocationState | null | undefined)?.authMode;
  const [mode, setMode] = useState<AuthMode>(() => getDefaultMode(modeScope));
  const [registerForm, setRegisterForm] = useState(createEmptyCredentials());
  const [loginForm, setLoginForm] = useState(createEmptyCredentials());
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotStatus, setForgotStatus] = useState<string | null>(null);
  const [agencyCompliance, setAgencyCompliance] = useState(createEmptyAgencyCompliance());
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const dynamicSectionRef = useRef<HTMLDivElement | null>(null);
  const lastTrackedAuthViewRef = useRef('');
  const [dynamicSectionHeight, setDynamicSectionHeight] = useState<number | undefined>(undefined);
  const { getClientInformation, registerWithUsernameAndPassword, loginWithUsernameAndPassword } = useAuthClient();
  const [forgotPassword] = useForgotPasswordUsernameAndPasswordMutation();
  const authSource = getAuthSource(location.pathname, modeScope);

  const shouldShowPasswordRequirements =
    mode === 'register' && showPasswordRequirements && !meetsPasswordRequirements(registerForm.password);
  const showModeSelector = modeScope === 'both';
  const showForgotPassword = mode === 'login' || modeScope === 'both';
  const requiresAgencyCompliance = mode === 'register' && accountType === AccountTypes.Agency;

  const activeError = useMemo(() => {
    if (showForgot) {
      return forgotError;
    }

    return mode === 'register' ? registerError : loginError;
  }, [forgotError, loginError, mode, registerError, showForgot]);

  useEffect(() => {
    if ((requestedAuthMode === 'login' || requestedAuthMode === 'register') && isModeAllowed(modeScope, requestedAuthMode)) {
      resetTransientState();
      setShowForgot(false);
      setMode(requestedAuthMode);
      return;
    }

    const defaultMode = getDefaultMode(modeScope);
    if (mode !== defaultMode && !isModeAllowed(modeScope, mode)) {
      resetTransientState();
      setShowForgot(false);
      setMode(defaultMode);
    }
  }, [mode, modeScope, requestedAuthMode]);

  useEffect(() => {
    const node = dynamicSectionRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      setDynamicSectionHeight(node?.scrollHeight);
      return undefined;
    }

    const updateHeight = () => setDynamicSectionHeight(node.scrollHeight);
    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(node);

    return () => observer.disconnect();
  }, [mode, registerForm.password, registerForm.confirmPassword, agencyCompliance, showForgot, activeError, forgotStatus]);

  useEffect(() => {
    if (showPasswordRequirements && meetsPasswordRequirements(registerForm.password)) {
      setShowPasswordRequirements(false);
    }
  }, [registerForm.password, showPasswordRequirements]);

  useEffect(() => {
    const viewKey = `${authSource}:${mode}:${showForgot}`;
    if (lastTrackedAuthViewRef.current === viewKey) {
      return;
    }

    lastTrackedAuthViewRef.current = viewKey;
    trackAnalyticsEvent(
      showForgot ? 'password_reset_started' : mode === 'register' ? 'registration_started' : 'login_started',
      {
        source: authSource,
      }
    );
  }, [authSource, mode, showForgot]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    const complianceError = validateAgencyCompliance();
    if (complianceError) {
      setRegisterError(complianceError);
      return;
    }

    trackAnalyticsEvent(mode === 'register' ? 'registration_submitted' : 'login_submitted', {
      source: authSource,
      method: 'google',
    });

    try {
      await getClientInformation(credentialResponse, mode, accountType, buildAgencyCompliancePayload());
    } catch (error) {
      if (mode === 'login') {
        if (isNotFoundError(error)) {
          navigate('/quick-setup', { state: { authMode: 'register' } });
          return;
        }

        setLoginError(getApiErrorMessage(error));
        setMode('login');
        return;
      }

      setRegisterError(isConflictError(error) ? 'Account already exists. Please sign in.' : getApiErrorMessage(error));
      setMode('register');
    } finally {
      setIsGoogleSigningIn(false);
    }
  };

  const validateAgencyCompliance = () => {
    if (!requiresAgencyCompliance) {
      return null;
    }

    if (!agencyCompliance.companyName.trim()) {
      return 'Company name is required for agency registration.';
    }

    if (!agencyCompliance.acceptedBusinessOnlyTerms) {
      return 'Confirm that you are registering for business use before creating an agency account.';
    }

    if (!agencyCompliance.acceptedTerms) {
      return 'Accept the current terms before creating an agency account.';
    }

    return null;
  };

  const buildAgencyCompliancePayload = (): RegistrationCompliancePayload | undefined => {
    if (!requiresAgencyCompliance) {
      return undefined;
    }

    return {
      companyName: agencyCompliance.companyName.trim(),
      acceptedTerms: agencyCompliance.acceptedTerms,
      acceptedBusinessOnlyTerms: agencyCompliance.acceptedBusinessOnlyTerms,
      acceptedTermsVersion: AgencyTermsAcceptance.version,
      acceptedTermsDocumentSet: AgencyTermsAcceptance.documentSet,
    };
  };

  const setCurrentModeError = (message: string) => {
    if (mode === 'register') {
      setRegisterError(message || null);
      return;
    }

    setLoginError(message || null);
  };

  const resetTransientState = () => {
    setRegisterError(null);
    setLoginError(null);
    setForgotError(null);
    setForgotStatus(null);
    setShowPasswordRequirements(false);
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
    setIsGoogleSigningIn(false);
  };

  const syncRouteAuthMode = (nextMode: AuthMode) => {
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state as Record<string, unknown> | null | undefined), authMode: nextMode },
    });
  };

  const handleModeChange = (nextMode: AuthMode) => {
    if (!isModeAllowed(modeScope, nextMode)) {
      return;
    }

    resetTransientState();
    setShowForgot(false);
    setMode(nextMode);
    syncRouteAuthMode(nextMode);
    trackAnalyticsEvent('auth_mode_selected', {
      source: authSource,
      mode: nextMode,
    });
  };

  const handleRegisterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRegisterError(null);

    if (!meetsPasswordRequirements(registerForm.password)) {
      setShowPasswordRequirements(true);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError('Passwords do not match.');
      return;
    }

    const complianceError = validateAgencyCompliance();
    if (complianceError) {
      setRegisterError(complianceError);
      return;
    }

    setIsRegistering(true);
    trackAnalyticsEvent('registration_submitted', {
      source: authSource,
      method: 'password',
    });

    try {
      await registerWithUsernameAndPassword({
        email: registerForm.email,
        password: registerForm.password,
        accountType,
        ...buildAgencyCompliancePayload(),
      });
      setRegisterForm(createEmptyCredentials());
      setAgencyCompliance(createEmptyAgencyCompliance());
    } catch (error) {
      setRegisterError(isConflictError(error) ? 'Account already exists. Please sign in.' : getApiErrorMessage(error));
    } finally {
      setIsRegistering(false);
    }
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginError(null);
    setIsLoggingIn(true);
    trackAnalyticsEvent('login_submitted', {
      source: authSource,
      method: 'password',
    });

    try {
      await loginWithUsernameAndPassword(loginForm);
      setLoginForm(createEmptyCredentials());
    } catch (error) {
      if (isNotFoundError(error)) {
        setLoginError('No account found. Please sign up.');
        return;
      }

      if (isUnauthorizedError(error)) {
        setLoginError('Your email address or password is incorrect.');
      } else {
        setLoginError(getApiErrorMessage(error));
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleForgotPasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setForgotError(null);
    setForgotStatus(null);
    setIsSendingReset(true);
    trackAnalyticsEvent('password_reset_requested', {
      source: authSource,
    });

    try {
      const response = await forgotPassword({ email: forgotForm.email }).unwrap();
      setForgotStatus(response);
      trackAnalyticsEvent('password_reset_request_completed', {
        source: authSource,
      });
    } catch (error) {
      setForgotError(getApiErrorMessage(error));
      trackAnalyticsEvent('password_reset_request_failed', {
        source: authSource,
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const authPanelClassName = [
    authPanelVariant === 'card' ? setupStyles.preflightCard : styles.authPanelPlain,
    styles.authPanel,
    !showModeSelector ? styles.singleAuthPanel : '',
    authPanelExtraClassName,
  ].filter(Boolean).join(' ');

  const renderInlineGooglePanel = (placement: 'beforeForm' | 'afterForm') => (
    <div className={styles.inlineGooglePanel}>
      {placement === 'beforeForm' && renderGoogleButton()}
      <div className={styles.dividerRow}>
        <span>or</span>
      </div>
      {placement === 'afterForm' && renderGoogleButton()}
      {inlineNote && (
        <div className={styles.inlineNote}>
          {inlineNote}
        </div>
      )}
    </div>
  );

  const renderPasswordToggle = (
    isVisible: boolean,
    onClick: () => void,
    label = 'password'
  ) => (
    <button
      type="button"
      className={styles.passwordToggleButton}
      onClick={onClick}
      aria-label={`${isVisible ? 'Hide' : 'Show'} ${label}`}
      aria-pressed={isVisible}
    >
      <i className={`fas ${isVisible ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
    </button>
  );

  const renderAuthForm = () => (
    <div className={authPanelClassName}>
      {showAuthPanelHeader && (
        <div className={setupStyles.preflightCardHeader}>
          <div>
            <span className="bc-eyebrow">
              {showForgot ? 'Password reset' : authPanelEyebrow ?? (mode === 'register' ? 'New workspace' : 'Welcome back')}
            </span>
            <h3>
              {showForgot ? 'Reset your password' : authPanelTitle ?? (mode === 'register' ? 'Email and password' : 'Sign in to your portal')}
            </h3>
          </div>
        </div>
      )}

      {!showModeSelector && !showForgot && googlePlacement === 'beforeForm' && renderInlineGooglePanel('beforeForm')}

      {!showForgot ? (
        <form className={styles.authForm} onSubmit={mode === 'register' ? handleRegisterSubmit : handleLoginSubmit}>
          <div className={styles.fieldGroup}>
            <label htmlFor="auth-email">Email address</label>
            <input
              id="auth-email"
              type="email"
              autoComplete="email"
              placeholder="you@yourbusiness.com"
              value={mode === 'register' ? registerForm.email : loginForm.email}
              onChange={(event) =>
                mode === 'register'
                  ? setRegisterForm((current) => ({ ...current, email: event.target.value }))
                  : setLoginForm((current) => ({ ...current, email: event.target.value }))
              }
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="auth-password">Password</label>
            <div className={styles.passwordField}>
              <input
                id="auth-password"
                type={isPasswordVisible ? 'text' : 'password'}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                placeholder={mode === 'register' ? 'Create a password' : 'Enter your password'}
                value={mode === 'register' ? registerForm.password : loginForm.password}
                onChange={(event) =>
                  mode === 'register'
                    ? setRegisterForm((current) => ({ ...current, password: event.target.value }))
                    : setLoginForm((current) => ({ ...current, password: event.target.value }))
                }
                required
              />
              {renderPasswordToggle(isPasswordVisible, () => setPasswordVisible((current) => !current))}
            </div>
          </div>

          {mode === 'register' && (
            <div className={styles.fieldGroup}>
              <label htmlFor="auth-confirm-password">Confirm password</label>
              <div className={styles.passwordField}>
                <input
                  id="auth-confirm-password"
                  type={isConfirmPasswordVisible ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirm your password"
                  value={registerForm.confirmPassword}
                  onChange={(event) =>
                    setRegisterForm((current) => ({ ...current, confirmPassword: event.target.value }))
                  }
                  required
                />
                {renderPasswordToggle(
                  isConfirmPasswordVisible,
                  () => setConfirmPasswordVisible((current) => !current),
                  'confirm password'
                )}
              </div>
            </div>
          )}

          {requiresAgencyCompliance && (
            <div className={styles.agencyCompliance}>
              <div className={styles.fieldGroup}>
                <label htmlFor="agency-company-name">Your company name</label>
                <input
                  id="agency-company-name"
                  type="text"
                  autoComplete="organization"
                  placeholder="Company or trading name"
                  value={agencyCompliance.companyName}
                  onChange={(event) =>
                    setAgencyCompliance((current) => ({ ...current, companyName: event.target.value }))
                  }
                  required
                />
              </div>

              <label className={styles.checkboxField}>
                <input
                  type="checkbox"
                  checked={agencyCompliance.acceptedBusinessOnlyTerms}
                  onChange={(event) =>
                    setAgencyCompliance((current) => ({
                      ...current,
                      acceptedBusinessOnlyTerms: event.target.checked,
                    }))
                  }
                />
                <span>
                  I confirm I am registering for business use and will only use agency access for business purposes.
                </span>
              </label>

              <label className={styles.checkboxField}>
                <input
                  type="checkbox"
                  checked={agencyCompliance.acceptedTerms}
                  onChange={(event) =>
                    setAgencyCompliance((current) => ({ ...current, acceptedTerms: event.target.checked }))
                  }
                />
                <span>
                  I accept the current <Link to="/terms" target="_blank" rel="noreferrer">Terms</Link>,{' '}
                  <Link to="/reseller-terms" target="_blank" rel="noreferrer">Reseller Terms</Link>,{' '}
                  <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Notice</Link>,{' '}
                  <Link to="/data-processing-agreement" target="_blank" rel="noreferrer">DPA</Link>, and{' '}
                  <Link to="/acceptable-use" target="_blank" rel="noreferrer">Acceptable Use Policy</Link>.
                </span>
              </label>
            </div>
          )}

          {activeError && <div className={styles.errorMessage}>{activeError}</div>}
          {shouldShowPasswordRequirements && (
            <div className={styles.helperMessage}>{PASSWORD_REQUIREMENTS_MESSAGE}</div>
          )}

          <div className={styles.formActions}>
            {mode === 'register' && registerBackAction && (
              <button type="button" className={styles.secondaryButton} onClick={registerBackAction.onClick}>
                {registerBackAction.label ?? 'Back'}
              </button>
            )}

            <button type="submit" className={styles.primaryButton} disabled={mode === 'register' ? isRegistering : isLoggingIn}>
              {mode === 'register'
                ? isRegistering ? 'Creating account...' : 'Create account'
                : isLoggingIn ? 'Signing in...' : 'Sign in'}
            </button>

            {showForgotPassword && (
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => {
                  setForgotForm({ email: loginForm.email || registerForm.email });
                  setShowForgot(true);
                  setForgotError(null);
                  setForgotStatus(null);
                }}
              >
                Forgot password
              </button>
            )}
          </div>
        </form>
      ) : (
        <form className={styles.authForm} onSubmit={handleForgotPasswordSubmit}>
          <div className={styles.fieldGroup}>
            <label htmlFor="forgot-email">Email address</label>
            <input
              id="forgot-email"
              type="email"
              autoComplete="email"
              placeholder="you@yourbusiness.com"
              value={forgotForm.email}
              onChange={(event) => setForgotForm({ email: event.target.value })}
              required
            />
          </div>

          {forgotError && <div className={styles.errorMessage}>{forgotError}</div>}
          {forgotStatus && <div className={styles.statusMessage}>{forgotStatus}</div>}

          <div className={styles.formActions}>
            <button type="submit" className={styles.primaryButton} disabled={isSendingReset}>
              {isSendingReset ? 'Sending link...' : 'Send reset link'}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={() => setShowForgot(false)}>
              Back
            </button>
          </div>
        </form>
      )}

      {!showModeSelector && !showForgot && googlePlacement === 'afterForm' && renderInlineGooglePanel('afterForm')}
    </div>
  );

  const renderGoogleButton = () => (
    <div className={styles.googleWrap}>
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={() => {
          setIsGoogleSigningIn(false);
          setCurrentModeError('Google sign-in could not be completed. Please try again.');
        }}
        theme="outline"
        size="large"
        shape="pill"
        text={mode === 'register' ? 'signup_with' : 'signin_with'}
        width="320"
      />
      {isGoogleSigningIn && (
        <div className={styles.googleStatus} role="status">
          Signing in with Google...
        </div>
      )}
    </div>
  );

  const renderGoogleSupportCard = () => (
    <div className={`${setupStyles.preflightCard} ${styles.authSupportCard}`}>
      <div className={setupStyles.preflightCardHeader}>
        <div>
          <span className="bc-eyebrow">Fastest option</span>
          <h3>Continue with Google</h3>
        </div>
      </div>

      <p className={styles.supportCopy}>
        Use Google for the quickest route into setup.
      </p>

      {renderGoogleButton()}

      <div className={styles.inlineNote}>
        {inlineNote}
      </div>
    </div>
  );

  return (
    <GoogleOAuthProvider clientId="603581137848-7hic62rk13in9b24emmgs4hbsd9e1cme.apps.googleusercontent.com">
      <div className={`${frameVariant === 'default' ? setupStyles.preflightFrame : styles.authFrameBare} ${styles.authFrame} ${className ?? ''}`.trim()}>
        {showIntro && (
          <div className={setupStyles.preflightHeader}>
            <span className="bc-eyebrow">{stepLabel}</span>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
        )}

        {showModeSelector && (
          <div className={setupStyles.preflightModeGrid}>
            <button
              type="button"
              className={`${setupStyles.preflightModeCard} ${mode === 'register' ? setupStyles.isSelected : ''}`}
              onClick={() => handleModeChange('register')}
            >
              {mode === 'register' && <span className={setupStyles.preflightSelectedBadge}>Selected</span>}
              <span className={setupStyles.preflightModeIcon}>
                <i className="fas fa-user-plus" />
              </span>
              <strong>Create an account</strong>
              <p>Best for first-time setup.</p>
            </button>

            <button
              type="button"
              className={`${setupStyles.preflightModeCard} ${mode === 'login' ? setupStyles.isSelected : ''}`}
              onClick={() => handleModeChange('login')}
            >
              {mode === 'login' && <span className={setupStyles.preflightSelectedBadge}>Selected</span>}
              <span className={setupStyles.preflightModeIcon}>
                <i className="fas fa-right-to-bracket" />
              </span>
              <strong>Sign in</strong>
              <p>Use this if you already have a workspace.</p>
            </button>
          </div>
        )}

        <div
          className={setupStyles.preflightDynamicSection}
          style={dynamicSectionHeight ? { height: dynamicSectionHeight } : undefined}
        >
          <div ref={dynamicSectionRef} className={styles.authDynamicInner}>
            {showModeSelector ? (
              <div className={styles.authGrid}>
                {renderAuthForm()}
                {renderGoogleSupportCard()}
              </div>
            ) : (
              <div className={styles.singleAuthGrid}>
                {renderAuthForm()}
              </div>
            )}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default StepLogin;
